import { useState } from 'react';

/**
 * Browser Notification — the real, working channel (as opposed to
 * "Push", which is device/FCM-based and needs org-supplied credentials
 * to ever fire — see Notification Settings). Generalizes the pattern
 * `features/chat/pages/ChatPage.tsx` already used for new-message
 * desktop alerts (foreground `Notification` API, gated on
 * `document.hidden` + an explicit permission grant, never
 * auto-requested) so any part of the app can show one, not just chat.
 * There is no service worker / VAPID push subscription behind this —
 * it only fires while the tab is open, which is what "browser
 * notification" means in most product contexts and is achievable
 * without a third-party push service.
 */
export function useBrowserNotifications() {
  const [granted, setGranted] = useState(typeof Notification !== 'undefined' && Notification.permission === 'granted');
  const isSupported = typeof Notification !== 'undefined';

  function requestPermission() {
    if (!isSupported) return;
    void Notification.requestPermission().then((permission) => setGranted(permission === 'granted'));
  }

  /** Only shows while the tab is hidden/backgrounded — a focused tab already has the in-app UI visible. */
  function notify(title: string, body?: string, onClick?: () => void) {
    if (!isSupported || !granted || !document.hidden) return;
    const notification = new Notification(title, { body });
    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
      };
    }
  }

  return { isSupported, granted, requestPermission, notify };
}
